import { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import { parseAbiItem } from "viem";
import { userSmartAccount, userPublicClient } from "../../components/RecoilAtom";
import { RAIN_ARBI_CONTRACT_ADDRESS, usdr_token } from "../../utils/Enviroment";

const TRANSFER_EVENT = parseAbiItem(
    "event Transfer(address indexed from, address indexed to, uint256 value)"
);

// Watch USDR and RAIN transfers into the smart account while a deposit modal is
// open (plus a grace period after it closes) and call onReceived on arrival.
// USDT is not watched: it is no longer a direct deposit asset, and USDT sent
// through the converter arrives as USDR.
export const useIncomingDepositWatcher = (active, onReceived, graceMs = 120_000) => {
    const smartAccount = useRecoilValue(userSmartAccount);
    const publicClient = useRecoilValue(userPublicClient);
    const seenRef = useRef(new Set());
    const unwatchRef = useRef([]);
    const teardownTimerRef = useRef(null);
    const onReceivedRef = useRef(onReceived);

    useEffect(() => {
        onReceivedRef.current = onReceived;
    }, [onReceived]);

    useEffect(() => {
        if (!smartAccount || !publicClient?.watchEvent) return;

        const stop = () => {
            unwatchRef.current.forEach((unwatch) => unwatch?.());
            unwatchRef.current = [];
        };

        if (!active) {
            if (unwatchRef.current.length && !teardownTimerRef.current) {
                teardownTimerRef.current = setTimeout(() => {
                    teardownTimerRef.current = null;
                    stop();
                }, graceMs);
            }
            return;
        }

        if (teardownTimerRef.current) {
            clearTimeout(teardownTimerRef.current);
            teardownTimerRef.current = null;
        }
        if (unwatchRef.current.length) return;

        const handleLogs = (logs) => {
            for (const log of logs) {
                const key = `${log?.transactionHash}:${log?.logIndex}`;
                if (!log?.transactionHash || seenRef.current.has(key)) continue;
                seenRef.current.add(key);
                if (!log?.args?.value) continue;
                onReceivedRef.current?.();
            }
        };

        unwatchRef.current = [usdr_token, RAIN_ARBI_CONTRACT_ADDRESS]
            .filter(Boolean)
            .map((address) =>
                publicClient.watchEvent({
                    address,
                    event: TRANSFER_EVENT,
                    args: { to: smartAccount },
                    onLogs: handleLogs,
                    onError: (error) => console.warn("[deposit-watcher] log subscription failed:", error),
                })
            );
    }, [active, smartAccount, publicClient, graceMs]);

    useEffect(
        () => () => {
            if (teardownTimerRef.current) clearTimeout(teardownTimerRef.current);
            unwatchRef.current.forEach((unwatch) => unwatch?.());
            unwatchRef.current = [];
        },
        []
    );
};
